import {
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import { fmtDurationLabel, fmtMinutes } from "../lib/peakCurves";
import { CHART } from "../lib/designTokens";

const CHART_H = 200;

const TooltipBox = ({
  active,
  payload,
  unit,
  isPeak,
  valueDecimals = 0,
  weightKg = null,
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const label = isPeak ? fmtDurationLabel(row.seconds) : row.range;
  const val = payload[0].value;
  const kg = Number(weightKg);
  const wkg =
    isPeak && val != null && unit === "W" && Number.isFinite(kg) && kg > 0
      ? (val / kg).toFixed(1)
      : null;
  const formatted = isPeak
    ? val != null
      ? wkg != null
        ? `${valueDecimals > 0 ? Number(val).toFixed(valueDecimals) : Math.round(val)} ${unit} / ${wkg} W/kg`
        : `${valueDecimals > 0 ? Number(val).toFixed(valueDecimals) : Math.round(val)} ${unit}`
      : "—"
    : fmtMinutes(val);
  return (
    <div className="chart-tooltip font-mono">
      <div className="text-dark-text-muted mb-1">{label}</div>
      <div className="text-dark-text-primary">{formatted}</div>
    </div>
  );
};

export const ZonePanel = ({ title, unit, zones, testId }) => (
  <div className="app-card-elevated p-5 sm:p-6" data-testid={testId}>
    <div className="card-label">Zones</div>
    <h3 className="section-title mt-0.5">
      {title} By Zones
    </h3>

    <div className="mt-4" style={{ height: CHART_H }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={zones.map((z, i) => ({ ...z, zoneLabel: String(i + 1) }))}
          margin={{ top: 10, right: 8, left: -8, bottom: 4 }}
        >
          <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="zoneLabel"
            tick={{ fontSize: 11, fill: CHART.tick, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={{ stroke: CHART.axis }}
          />
          <YAxis
            tickFormatter={(v) => `${Math.round(v / 60)}m`}
            tick={{ fontSize: 10, fill: CHART.tick, fontFamily: "JetBrains Mono" }}
            tickLine={false}
            axisLine={{ stroke: CHART.axis }}
            width={40}
          />
          <Tooltip
            cursor={{ fill: CHART.cursorFill }}
            content={<TooltipBox unit={unit} />}
          />
          <Bar dataKey="seconds" radius={[2, 2, 0, 0]}>
            {zones.map((z, i) => (
              <Cell key={i} fill={z.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>

    <div className="mt-4">
      {zones.map((z, i) => (
        <div
          key={i}
          className="flex items-center justify-between py-2 text-xs font-mono border-b border-[var(--border-primary)]/60 last:border-b-0"
        >
          <span className="text-dark-text-muted w-5">{i + 1}</span>
          <span className="text-dark-text-primary flex-1 pl-3 truncate">{z.range}</span>
          <span className="text-dark-text-secondary tabular-nums">{fmtMinutes(z.seconds)}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PeakCurvePanel = ({
  title,
  unit,
  curve,
  color,
  testId,
  valueDecimals = 0,
  weightKg = null,
}) => {
  const fmtVal = (v) =>
    v != null
      ? valueDecimals > 0
        ? Number(v).toFixed(valueDecimals)
        : String(Math.round(v))
      : "—";
  const fmtWkg = (watts) => {
    const kg = Number(weightKg);
    if (watts == null || !Number.isFinite(kg) || kg <= 0) return null;
    return (watts / kg).toFixed(1);
  };
  const fmtPeakValue = (v) => {
    if (v == null) return "—";
    const wkg = fmtWkg(v);
    return wkg != null ? `${fmtVal(v)} ${unit} / ${wkg} W/kg` : `${fmtVal(v)} ${unit}`;
  };
  if (!curve?.length) {
    return (
      <div className="app-card-elevated p-5 sm:p-6" data-testid={testId}>
        <div className="card-label">Best</div>
        <h3 className="section-title mt-0.5" style={{ color }}>
          Peak {title}
        </h3>
        <p className="mt-6 text-xs text-dark-text-secondary">
          No data recorded for this metric.
        </p>
      </div>
    );
  }

  const minSec = curve[0].seconds;
  const maxSec = curve[curve.length - 1].seconds;

  return (
    <div className="app-card-elevated p-5 sm:p-6" data-testid={testId}>
      <div className="card-label">Best</div>
      <h3 className="section-title mt-0.5" style={{ color }}>
        Peak {title}
      </h3>

      <div className="mt-4" style={{ height: CHART_H }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={curve}
            margin={{ top: 10, right: 8, left: -8, bottom: 4 }}
          >
            <CartesianGrid stroke={CHART.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="seconds"
              type="number"
              scale="log"
              domain={[minSec, maxSec]}
              ticks={[5, 30, 60, 300, 1200, 3600, 7200, 14400].filter(
                (t) => t >= minSec && t <= maxSec
              )}
              tickFormatter={(v) => fmtDurationLabel(v)}
              tick={{ fontSize: 10, fill: CHART.tick, fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={{ stroke: CHART.axis }}
              allowDataOverflow
            />
            <YAxis
              tick={{ fontSize: 10, fill: CHART.tick, fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={{ stroke: CHART.axis }}
              width={40}
            />
            <Tooltip
              cursor={{ stroke: color, strokeWidth: 1, strokeDasharray: "3 3" }}
              content={
                <TooltipBox
                  unit={unit}
                  isPeak
                  valueDecimals={valueDecimals}
                  weightKg={weightKg}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              fill={color}
              fillOpacity={0.3}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-6 text-xs font-mono">
        {curve.map((c, i) => (
          <div
            key={i}
            className="flex justify-between gap-2 border-b border-[var(--border-primary)]/60 py-1 min-w-0"
          >
            <span className="text-dark-text-muted shrink-0">{fmtDurationLabel(c.seconds)}</span>
            <span className="text-dark-text-primary tabular-nums text-right min-w-0">
              {fmtPeakValue(c.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
